import { create } from 'zustand';

/** Chrome's install prompt event (not in the DOM typings). */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallState {
  /** The browser offered to install; null until it does, or once used. */
  prompt: InstallPromptEvent | null;
  installed: boolean;
  install: () => Promise<boolean>;
}

function runningInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  const standalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standalone || window.matchMedia?.('(display-mode: standalone)').matches === true;
}

/** iPhone and iPad Safari never offer a prompt; people add the app from the Share menu. */
export function needsManualInstall(userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent): boolean {
  const apple = /iPhone|iPad|iPod/i.test(userAgent) || (/Macintosh/.test(userAgent) && /Mobile/.test(userAgent));
  return apple && !runningInstalled();
}

export const useInstall = create<InstallState>()((set, get) => ({
  prompt: null,
  installed: runningInstalled(),
  install: async () => {
    const event = get().prompt;
    if (!event) return false;
    await event.prompt();
    const { outcome } = await event.userChoice;
    set({ prompt: null, installed: outcome === 'accepted' });
    return outcome === 'accepted';
  },
}));

// The browser may offer before React has rendered anything, so listen from the start.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    useInstall.setState({ prompt: event as InstallPromptEvent });
  });
  window.addEventListener('appinstalled', () => useInstall.setState({ prompt: null, installed: true }));
}
